/**
 * Size Guide Popup Handler
 * Handles unit toggle (CM/IN) and collapsible sections
 *
 * This file can be requested by several snippets on the same page
 * (product-variant-options, size-guide-popup, size-guide-popup-2), and classic
 * scripts re-execute on every duplicate tag. Everything lives inside an IIFE
 * behind a one-shot flag so a repeat execution is a no-op instead of a
 * top-level redeclaration error.
 */
(function () {
  if (window.__sizeGuidePopupLoaded) return;
  window.__sizeGuidePopupLoaded = true;

class SizeGuidePopup {
  constructor(popup) {
    this.popup = popup || document.querySelector("#PopupModal-sizeguide");
    if (!this.popup) return;

    // Grab this modal's own underlay before it gets moved to <body>.
    this.underlay =
      this.popup.nextElementSibling &&
      this.popup.nextElementSibling.matches("[data-sizeguide-underlay]")
        ? this.popup.nextElementSibling
        : document.querySelector("[data-sizeguide-underlay]");
    if (this.underlay) document.body.appendChild(this.underlay);
    this.toggleInput = this.popup.querySelector("[data-unit-toggle]");
    this.labelCm = this.popup.querySelector(
      ".popup-sizeguide__toggle-label--cm",
    );
    this.labelIn = this.popup.querySelector(
      ".popup-sizeguide__toggle-label--in",
    );
    this.sections = this.popup.querySelectorAll("[data-collapsible]");
    this.currentUnit = "cm";

    this.init();
  }

  init() {
    // Prevent modal close when clicking inside container
    const container = this.popup.querySelector(".popup-sizeguide__container");
    if (container) {
      container.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }

    // Handle table scroll for gradient visibility
    this.initTableScrollGradient();

    // Close button handler
    const closeBtn = this.popup.querySelector(".drawer__close");
    if (closeBtn) {
      closeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.popup.hide();
      });
    }

    // Underlay click to close
    if (this.underlay) {
      this.underlay.addEventListener("click", () => {
        this.popup.hide();
      });
    }

    // Unit toggle checkbox
    if (this.toggleInput) {
      this.toggleInput.addEventListener("change", (e) => {
        e.stopPropagation();
        const unit = this.toggleInput.checked ? "in" : "cm";
        this.switchUnit(unit);
      });
    }

    // Collapsible sections
    this.sections.forEach((section) => {
      const header = section.querySelector("[data-collapsible-header]");
      if (header) {
        header.addEventListener("click", (e) => {
          e.stopPropagation();
          this.toggleSection(section);
        });
      }
    });

    // Restore saved unit preference
    this.restoreUnitPreference();

    // Listen for popup open to restore unit
    const observer = new MutationObserver(() => {
      if (this.popup.hasAttribute("open")) {
        this.restoreUnitPreference();
        // Show underlay
        if (this.underlay) {
          this.underlay.classList.add("is-visible");
        }
      } else {
        // Hide underlay
        if (this.underlay) {
          this.underlay.classList.remove("is-visible");
        }
      }
    });

    observer.observe(this.popup, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }

  switchUnit(unit) {
    if (this.currentUnit === unit) return;

    this.currentUnit = unit;

    // Update toggle state
    if (this.toggleInput) {
      this.toggleInput.checked = unit === "in";
    }

    // Update label states
    if (this.labelCm && this.labelIn) {
      if (unit === "cm") {
        this.labelCm.classList.add("active");
        this.labelIn.classList.remove("active");
      } else {
        this.labelCm.classList.remove("active");
        this.labelIn.classList.add("active");
      }
    }

    // Update table cell values based on unit
    this.updateTableValues(unit);

    // Store preference
    try {
      sessionStorage.setItem("sizeGuideUnit", unit);
    } catch (e) {
      console.warn("Could not save unit preference:", e);
    }
  }

  updateTableValues(unit) {
    // Find all cells with dual unit data attributes
    const cells = this.popup.querySelectorAll("td[data-cm][data-in]");

    cells.forEach((cell) => {
      const cmValue = cell.getAttribute("data-cm");
      const inValue = cell.getAttribute("data-in");

      if (unit === "cm") {
        cell.textContent = cmValue;
      } else {
        cell.textContent = inValue;
      }
    });
  }

  toggleSection(section) {
    const isOpen = section.classList.contains("is-open");

    if (isOpen) {
      section.classList.remove("is-open");
    } else {
      section.classList.add("is-open");
    }
  }

  restoreUnitPreference() {
    try {
      const savedUnit = sessionStorage.getItem("sizeGuideUnit");
      if (savedUnit && (savedUnit === "cm" || savedUnit === "in")) {
        this.switchUnit(savedUnit);
      }
    } catch (e) {
      console.warn("Could not restore unit preference:", e);
    }
  }

  initTableScrollGradient() {
    const tableContainer = this.popup.querySelector(
      ".popup-sizeguide__table-container",
    );
    const tableWrapper = this.popup.querySelector(
      ".popup-sizeguide__table-wrapper",
    );

    if (!tableContainer || !tableWrapper) return;

    const checkScroll = () => {
      const scrollLeft = tableWrapper.scrollLeft;
      const scrollWidth = tableWrapper.scrollWidth;
      const clientWidth = tableWrapper.clientWidth;

      // Check if scrolled to the end (with 5px threshold)
      if (scrollLeft + clientWidth >= scrollWidth - 5) {
        tableContainer.classList.add("scrolled-to-end");
      } else {
        tableContainer.classList.remove("scrolled-to-end");
      }
    };

    // Check on scroll
    tableWrapper.addEventListener("scroll", checkScroll);

    // Check on load and when popup opens
    checkScroll();

    // Re-check when unit changes (table width might change)
    const originalSwitchUnit = this.switchUnit.bind(this);
    this.switchUnit = function (unit) {
      originalSwitchUnit(unit);
      setTimeout(checkScroll, 50);
    };
  }
}

// Initialize when DOM is ready
function initSizeGuidePopup() {
  // A product page can carry more than one #PopupModal-sizeguide (tabs block +
  // variant selector). Wire each one up rather than only the first match.
  document
    .querySelectorAll("#PopupModal-sizeguide")
    .forEach(function (popup) {
      new SizeGuidePopup(popup);
    });

  // Fallback: handle overlay for modal-opener triggers when SizeGuidePopup
  // couldn't observe the modal (e.g. popup not yet in DOM at init time)
  document.querySelectorAll('[data-modal="#PopupModal-sizeguide"]').forEach(function(opener) {
    opener.addEventListener("click", function() {
      var modal = document.querySelector("#PopupModal-sizeguide");
      var underlay = document.querySelector("[data-sizeguide-underlay]");
      if (!modal || !underlay) return;

      // If MutationObserver already handles it, this is a no-op
      requestAnimationFrame(function() {
        if (modal.hasAttribute("open")) {
          underlay.classList.add("is-visible");
          document.body.classList.add("overflow-hidden");
        }
      });
    });
  });
}

function initSizePopoutSoldoutToggle() {
  // Set initial state for any pre-selected sold-out size
  document.querySelectorAll('.select-popout--size').forEach(function(popout) {
    const activeItem = popout.querySelector('.select-popout__item.is-active');
    const toggle = popout.querySelector('[data-popout-toggle]');
    if (activeItem && toggle) {
      toggle.classList.toggle('is-soldout-selected', activeItem.classList.contains('select-popout__item--soldout'));
    }
  });

  // Handle option clicks via event delegation
  document.addEventListener('click', function(evt) {
    const option = evt.target.closest('.select-popout--size .select-popout__option');
    if (!option) return;
    const li = option.closest('.select-popout__item');
    const popout = option.closest('popout-select');
    if (!li || !popout) return;
    const toggle = popout.querySelector('[data-popout-toggle]');
    if (!toggle) return;
    toggle.classList.toggle('is-soldout-selected', li.classList.contains('select-popout__item--soldout'));
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", function() {
    initSizeGuidePopup();
    initSizePopoutSoldoutToggle();
  });
} else {
  initSizeGuidePopup();
  initSizePopoutSoldoutToggle();
}

})();
